import { PatchOrganizationSettingsRequest } from '@localstack/types';
import { ControlledTextField, ProgressButton, ControlledSwitch } from '@localstack/ui';
import { Box, Card, CardActions, CardContent, CardHeader, Typography } from '@mui/material';
import { ReactElement, useCallback, useEffect } from 'react';

import { OrganizationService, useApiGetter, useApiEffect, useSnackbar, VALIDATION_RULES } from '@localstack/services';
import { useForm } from 'react-hook-form';

type FormProps = {
  autoCleanup: boolean;
  maxVersions: number;
};

export const CloudPodsSettings = (): ReactElement => {
  const { showSnackbar } = useSnackbar();

  const { control, handleSubmit, setValue, watch } = useForm<FormProps>({ mode: 'all' });
  const isAutoCleanupEnabled = watch('autoCleanup');

  useEffect(() => {
    if (!isAutoCleanupEnabled) {
      setValue('maxVersions', 0, { shouldValidate: true });
    }
  }, [isAutoCleanupEnabled]);

  const { data: organization } = useApiGetter(OrganizationService, 'getOrganization', []);

  const { patchOrganizationSettings, isLoading: isOrgUpdating } = useApiEffect(
    OrganizationService,
    ['patchOrganizationSettings'],
    { revalidate: ['getOrganization'] },
  );

  useEffect(() => {
    if (!organization) return;
    const podsSettings = organization?.pods_settings;
    if (podsSettings?.auto_cleanup) {
      const isEnabled = !!podsSettings.auto_cleanup.max_pod_versions;
      setValue('maxVersions', podsSettings.auto_cleanup.max_pod_versions || 0, { shouldValidate: true });
      setValue('autoCleanup', isEnabled, { shouldValidate: true });
    }
  }, [organization?.id]);

  const updatePodsSettings = useCallback(
    async ({ autoCleanup, maxVersions }: FormProps) => {
      const maxVersion = autoCleanup ? maxVersions : 0;
      const requestBody: PatchOrganizationSettingsRequest = {
        pods_settings: {
          auto_cleanup_max_pod_versions: maxVersion,
        },
      };
      await patchOrganizationSettings(requestBody);
      showSnackbar({ severity: 'success', message: 'Successfully updated cloud pods settings' });
    },
    [organization],
  );

  return (
    <Card>
      <CardHeader title="Cloud Pods Settings" />
      <form onSubmit={handleSubmit(updatePodsSettings)}>
        <Card variant="outlined">
          <CardHeader title="Auto-cleanup" />
          <CardContent>
            <Typography variant="body2" paragraph>
              When enabled, old versions of Cloud Pods will be cleaned up automatically. All versions of a Cloud Pod
              that exceed the threshold set below will be deleted permanently. Note that changing this setting here will
              not automatically trigger a cleanup: eventual deletions will happen only when creating a new Cloud Pod
              version.
            </Typography>
            <ControlledSwitch control={control} name="autoCleanup" label="Auto-cleanup" labelPlacement="end" />
            <Box mb={3}>
              <ControlledTextField
                control={control}
                name="maxVersions"
                label="No. versions to retain (per pod)"
                placeholder="no. versions"
                required={isAutoCleanupEnabled}
                variant="outlined"
                rules={isAutoCleanupEnabled ? VALIDATION_RULES.integerOnly : undefined}
                disabled={!isAutoCleanupEnabled}
                style={{ width: 220 }}
              />
            </Box>
          </CardContent>
          <CardActions>
            <ProgressButton loading={isOrgUpdating} type="submit" color="primary" variant="contained">
              Save
            </ProgressButton>
          </CardActions>
        </Card>
      </form>
    </Card>
  );
};
